/**
 * Cấu hình Zalo theo từng phòng khám — API của console nền tảng.
 * Real /api/v1/platform/zalo-setup.
 *
 * Quản trị viên khai một dòng cho mỗi phòng khám rồi thôi. Khách hàng không lấy gì từ Zalo, không
 * chạy CLI, không dán token — bên Addy Sites họ chỉ sửa nội dung và bấm Phát hành.
 *
 * API **không bao giờ** trả giá trị bí mật: chỉ "có hay chưa" và bốn ký tự cuối của refresh token,
 * đủ để đối chiếu với chuỗi đang thấy trên Zalo, không đủ để dùng lại.
 */
import { apiClient } from "./client";

export interface PlatformZaloRow {
  tenantId: string;
  tenantCode: string;
  clinicName: string;
  /** Mini App ID đang liên kết, null nếu chưa. */
  zaloAppId: string | null;
  enabled: boolean;
  /** Đã khai Secret Key của Mini App chưa — thiếu thì bệnh nhân không đăng nhập được bằng Zalo. */
  hasPaymentPrivateKey: boolean;
  /**
   * Bệnh nhân đăng nhập được qua Mini App này chưa — backend tính (#19125).
   *
   * Màn này TỪNG tự ghép luật `zaloAppId && !hasAppSecret`. Một luật nằm ở hai nơi thì sớm muộn
   * hai nơi trả lời khác nhau; backend là nơi biết `RealZaloUserVerifier` ký bằng giá trị nào.
   */
  loginReady: boolean;
  oauthAppId: string | null;
  hasAppSecret: boolean;
  hasRefreshToken: boolean;
  refreshTokenHint: string | null;
  rotatedAt: string | null;
  updatedAt: string | null;
  /** Đủ để build hay chưa — câu hỏi duy nhất mỗi dòng cần trả lời. */
  buildable: boolean;
}

/** Ô để trống = giữ nguyên. Người dùng không gõ lại thứ họ không nhìn thấy. */
export interface PlatformZaloUpdate {
  zaloAppId?: string;
  oauthAppId?: string;
  appSecret?: string;
  refreshToken?: string;
  zaloPaymentPrivateKey?: string;
  /** Secret Key của MINI APP. Chỉ có đường vào; để trống = giữ nguyên. */
}

const BASE = "/v1/platform/zalo-setup";

export const platformZaloApi = {
  list(): Promise<PlatformZaloRow[]> {
    return apiClient.get<PlatformZaloRow[]>(BASE).then((r) => r.data);
  },
  save(tenantId: string, body: PlatformZaloUpdate): Promise<PlatformZaloRow> {
    return apiClient.put<PlatformZaloRow>(`${BASE}/${tenantId}`, body).then((r) => r.data);
  },
  /**
   * Kéo App ID + Secret từ Addy Message về bản sao chỉ đọc của HIS.
   *
   * Addy Message là nơi khai có thẩm quyền; HIS không tự sinh ra hai giá trị này. Hành động
   * tường minh chứ không phải nền: một job chạy ngầm để hai kho khớp nhau chính là thứ thiết
   * kế này tránh, và nó hỏng lặng lẽ — đúng kiểu hỏng đã tạo ra bản trùng lặp ban đầu.
   */
  syncAppIdentity(tenantId: string): Promise<PlatformZaloSyncResult> {
    return apiClient
      .post<PlatformZaloSyncResult>(`${BASE}/${tenantId}/sync-app-identity`)
      .then((r) => r.data);
  },
};

export interface PlatformZaloSyncResult {
  environment: string | null;
  appId: string | null;
  hasSecret: boolean;
  configured: boolean;
  changed: boolean;
}

export const platformZaloQueryKeys = {
  all: () => ["platform-zalo-setup"] as const,
};
